import type { ReactNode } from "react";
import styled from "styled-components";
import { Spin } from "antd";
import { Check, Clock3, DollarSign, FileText, List as ListIcon, Mail, Plus, ReceiptText, X } from "lucide-react";
import type { NotificationCounts, NotificationItem } from "@/api/notifications";
import paymentStatementBadgeIcon from "@/assets/icons/payment-statement-badge.svg";
import paymentStatementFileIcon from "@/assets/icons/payment-statement-file.svg";

const Panel = styled.aside`
	position: fixed;
	top: 8px;
	left: 88px;
	width: 465px;
	max-width: calc(100vw - 104px);
	height: calc(100vh - 16px);
	background: #ffffff;
	border-radius: 16px;
	box-shadow: 0px 8px 32px rgba(21, 89, 165, 0.16);
	z-index: 2100;
	display: flex;
	flex-direction: column;
	overflow: hidden;
`;

const Header = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 22px 24px 18px;
	flex-shrink: 0;
`;

const HeaderLeft = styled.div`
	display: flex;
	align-items: center;
	gap: 7px;
`;

const HeaderTitle = styled.div`
	font-family: Poppins, sans-serif;
	font-size: 14px;
	font-weight: 600;
	color: #303030;
`;

const HeaderCount = styled.span`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	min-width: 14px;
	height: 14px;
	padding: 0 3px;
	border-radius: 999px;
	background: #f72586;
	color: #fff;
	font:
		600 9px/14px Poppins,
		sans-serif;
`;

const CloseButton = styled.button`
	width: 32px;
	height: 32px;
	border: none;
	border-radius: 8px;
	background: transparent;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	color: #8a8a8a;

	&:hover {
		background: #f5f5f5;
	}
`;

const MarkAllButton = styled.button<{ $footer?: boolean }>`
	border: none;
	background: transparent;
	color: ${(p) => (p.$footer ? "#2a057f" : "#3a0ca3")};
	font-family: Poppins, sans-serif;
	font-size: ${(p) => (p.$footer ? "14px" : "13px")};
	font-weight: ${(p) => (p.$footer ? 600 : 500)};
	cursor: pointer;
	padding: ${(p) => (p.$footer ? "4px 8px" : "0")};

	&:hover {
		text-decoration: underline;
	}

	&:disabled {
		color: #b9b9b9;
		cursor: default;
		text-decoration: none;
	}
`;

const List = styled.div`
	flex: 1;
	overflow-y: auto;
	min-height: 0;
	padding: 4px 0 0;
`;

const Item = styled.button<{ $unread?: boolean; $paymentStatement?: boolean }>`
	width: 100%;
	border: none;
	border-bottom: 1px solid #f0f0f0;
	background: ${(p) => (p.$unread ? (p.$paymentStatement ? "#f9f6ff" : "#f6f4ff") : "#ffffff")};
	display: grid;
	grid-template-columns: 40px 1fr;
	column-gap: ${(p) => (p.$paymentStatement ? "9px" : "12px")};
	text-align: left;
	padding: 16px 24px;
	cursor: pointer;
	position: relative;

	&:hover {
		background: ${(p) => (p.$unread ? "#f0edff" : "#fafafa")};
	}
`;

const UnreadDot = styled.span`
	position: absolute;
	left: 9px;
	top: 25px;
	width: 6px;
	height: 6px;
	border-radius: 50%;
	background: #f72586;
	display: block;
`;

const PaymentStatementUnreadDot = styled(UnreadDot)`
	left: auto;
	right: 24px;
	top: 25px;
`;

const AvatarWrap = styled.div`
	position: relative;
	width: 40px;
	height: 40px;
	flex-shrink: 0;
`;

const Avatar = styled.div<{ $bg: string }>`
	width: 40px;
	height: 40px;
	border-radius: 50%;
	background: ${(p) => p.$bg};
	color: #ffffff;
	display: flex;
	align-items: center;
	justify-content: center;
	font-family: Poppins, sans-serif;
	font-size: 13px;
	font-weight: 600;
	overflow: hidden;

	img {
		width: 100%;
		height: 100%;
		object-fit: cover;
	}
`;

const AvatarBadge = styled.span<{ $bg: string }>`
	position: absolute;
	right: -1px;
	bottom: -1px;
	width: 17px;
	height: 17px;
	border-radius: 50%;
	background: ${(p) => p.$bg};
	border: 1.5px solid #ffffff;
	color: #ffffff;
	display: flex;
	align-items: center;
	justify-content: center;
`;

const ItemBody = styled.div`
	display: flex;
	flex-direction: column;
	min-width: 0;
`;

const Title = styled.div`
	font-family: Poppins, sans-serif;
	font-size: 13px;
	font-weight: 500;
	color: #303030;
	line-height: 18px;

	strong {
		font-weight: 700;
		color: #1a1a1a;
	}
`;

const Meta = styled.div`
	display: flex;
	align-items: center;
	gap: 6px;
	flex-wrap: wrap;
	min-height: 20px;
	font-family: Poppins, sans-serif;
	font-size: 11px;
	color: #9b9b9b;
	line-height: 17px;
`;

const MetaSep = styled.span`
	color: #cfcfcf;
`;

const Tag = styled.span<{ $tone: "creator" | "campaign" | "finance" }>`
	border-radius: 999px;
	padding: 1px 9px;
	height: 17px;
	font-size: 11px;
	font-weight: 500;
	line-height: 15px;
	background: ${(p) => (p.$tone === "creator" ? "#e8f3ff" : p.$tone === "finance" ? "#f3eefe" : "#eeeaff")};
	color: ${(p) => (p.$tone === "creator" ? "#2f6fed" : p.$tone === "finance" ? "#6b4eff" : "#5b4cdb")};
`;

const EmailMeta = styled.span`
	display: inline-flex;
	align-items: center;
	gap: 3px;
	color: #9b9b9b;
`;

const Body = styled.div`
	margin-top: 6px;
	font-family: Poppins, sans-serif;
	font-size: 12px;
	color: #707070;
	line-height: 17px;
`;

const PaymentSummary = styled.div`
	display: flex;
	align-items: center;
	gap: 4px;
	width: 100%;
	min-height: 38px;
	margin-top: 4px;
	padding: 9px 8px;
	border: 1px solid #eceffb;
	border-radius: 10px;
	background: #f7f8fd;
	box-sizing: border-box;
`;

const PaymentSummaryIcon = styled.span<{ $status: string }>`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 18px;
	height: 18px;
	flex: 0 0 18px;
	border-radius: 5px;
	background: ${({ $status }) =>
		$status === "completed" ? "#dff2cf" : $status === "on_hold" ? "#fff0c7" : "#fde1e6"};
	color: ${({ $status }) => ($status === "completed" ? "#4f9719" : $status === "on_hold" ? "#a66b00" : "#c81938")};
`;

const PaymentSummaryMain = styled.strong`
	min-width: 0;
	flex: 1;
	overflow: hidden;
	color: #303030;
	font:
		600 12px/18px Poppins,
		sans-serif;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const PaymentSummaryDate = styled.span`
	flex: 0 0 auto;
	color: #9e9eb5;
	font:
		400 11px/17px Poppins,
		sans-serif;
	white-space: nowrap;
`;

const PaymentReason = styled.div`
	margin-top: 4px;
	color: #6b6b80;
	font:
		400 12px/17px Poppins,
		sans-serif;
`;

const PaymentStatementSummary = styled.div`
	display: flex;
	align-items: center;
	gap: 4px;
	width: 100%;
	min-height: 38px;
	margin-top: 4px;
	padding: 9px 8px;
	border: 1px solid #eceffb;
	border-radius: 10px;
	background: #f7f8fd;
	box-sizing: border-box;
`;

const PaymentStatementFileIcon = styled.span`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 18px;
	height: 18px;
	flex: 0 0 18px;
	border-radius: 5px;
	background: rgba(162, 161, 161, 0.5);

	img {
		width: 14px;
		height: 14px;
	}
`;

const CtaRow = styled.div`
	display: flex;
	justify-content: flex-end;
	margin-top: 8px;
`;

const Cta = styled.span`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	height: 34px;
	padding: 0 12px;
	border-radius: 999px;
	border: 1px solid #ececec;
	background: #ffffff;
	font-family: Poppins, sans-serif;
	font-size: 13px;
	font-weight: 600;
	line-height: 18px;
	color: #f72586;
	white-space: nowrap;
`;

const Empty = styled.div`
	padding: 64px 24px;
	text-align: center;
	font-family: Poppins, sans-serif;
	color: #9b9b9b;
`;

const Footer = styled.div`
	flex-shrink: 0;
	padding: 11px 24px 18px;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 10px;
	border-top: 1px solid #f0f0f0;
	background: #ffffff;
`;

const LoadMore = styled.button`
	border: none;
	background: transparent;
	color: #3a0ca3;
	font-family: Poppins, sans-serif;
	cursor: pointer;
`;

type EmailStatus = "also" | "proposed" | null;

function formatRelativeTime(iso: string): string {
	const created = new Date(iso);
	const now = new Date();
	const diffMs = now.getTime() - created.getTime();
	const minutes = Math.floor(diffMs / 60000);
	if (minutes < 1) return "Just now";
	if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
	const hours = Math.floor(minutes / 60);
	if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;

	const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
	const startOfCreated = new Date(created.getFullYear(), created.getMonth(), created.getDate());
	const dayDiff = Math.round((startOfToday.getTime() - startOfCreated.getTime()) / 86_400_000);
	if (dayDiff === 1) return "Yesterday";
	if (dayDiff < 7) return `${dayDiff} days ago`;
	return created.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function tabTone(item: NotificationItem): "creator" | "campaign" | "finance" {
	if (item.type === "CS1") return "campaign";
	if (item.type === "C3" || item.tab === "campaign") return "campaign";
	if (item.type === "C4" || item.tab === "finance") return "finance";
	return "creator";
}

function tabLabel(item: NotificationItem): string {
	if (item.tag?.trim()) return item.tag.trim();
	const tone = tabTone(item);
	if (tone === "finance") return "Finance";
	if (tone === "campaign") return "Campaign";
	return "Creator";
}

function emailStatusFor(type: string): EmailStatus {
	if (["C1", "C4", "CL1", "CL2", "CS5", "CM5"].includes(type)) return "also";
	if (["C2", "CM2", "CM4"].includes(type)) return "proposed";
	return null;
}

function avatarStyle(item: NotificationItem): { bg: string; badgeBg: string; badge: ReactNode; fallback: ReactNode } {
	if (item.type === "CS1") {
		return {
			bg: "#f72586",
			badgeBg: "#22c55e",
			badge: <Check size={9} strokeWidth={3} />,
			fallback: item.actorInitials || "?",
		};
	}
	if (item.type === "CS2") {
		return {
			bg: "#3b82f6",
			badgeBg: "#e0102f",
			badge: <X size={9} strokeWidth={3} />,
			fallback: item.actorInitials || "?",
		};
	}
	if (item.type === "C2") {
		return {
			bg: "#e8a05a",
			badgeBg: "#8b5e3c",
			badge: <ListIcon size={9} strokeWidth={2.5} />,
			fallback: <FileText size={16} />,
		};
	}
	if (item.type === "C3" || item.type === "CS6") {
		return {
			bg: "#7c5cfc",
			badgeBg: "#22c55e",
			badge: <Check size={9} strokeWidth={3} />,
			fallback: item.actorInitials || "?",
		};
	}
	if (item.type === "CS4") {
		return {
			bg: "#e7e5ff",
			badgeBg: "#eee533",
			badge: <img src={paymentStatementBadgeIcon} alt="" width={9} height={9} />,
			fallback: item.actorInitials || "?",
		};
	}
	if (item.type === "C4" || item.tab === "finance") {
		const paid = item.payment?.status === "completed";
		const delayed = item.payment?.status === "on_hold";
		return {
			bg: "#e7e5ff",
			badgeBg: paid ? "#89d04a" : delayed ? "#e5ad32" : item.payment ? "#d30024" : "#6b4eff",
			badge: paid ? (
				<Check size={9} strokeWidth={3} />
			) : delayed ? (
				<Clock3 size={9} strokeWidth={2.5} />
			) : item.payment ? (
				<X size={9} strokeWidth={3} />
			) : (
				<DollarSign size={9} strokeWidth={2.5} />
			),
			fallback: <DollarSign size={16} />,
		};
	}
	return {
		bg: "#7c5cfc",
		badgeBg: "#3b82f6",
		badge: <Plus size={10} strokeWidth={3} />,
		fallback: item.actorInitials || "?",
	};
}

function displayTitle(item: NotificationItem): ReactNode {
	const campaign = item.campaignName?.trim();
	if (item.type === "C1" && campaign) {
		return (
			<>
				You've been added to <strong>{campaign}</strong>
			</>
		);
	}
	if (item.type === "CS1" || item.type === "CS2") {
		const name = item.actorName?.trim();
		if (name) {
			return (
				<>
					<strong>{name}</strong>{" "}
					{item.type === "CS1" ? "accepted the campaign invite" : "declined the campaign invite"}
				</>
			);
		}
		return item.title;
	}
	if (item.type === "C2") {
		return "Your Campaign Engagement Terms are ready for review";
	}
	if (item.type === "C3") {
		return (
			<>
				Your deliverable was <strong>approved</strong> by the Campaign Manager
			</>
		);
	}
	if (item.type === "CS6") {
		const match = item.title.match(/^(.+?) marked (.+) as complete$/);
		if (match) {
			return (
				<>
					<strong>{match[1]}</strong> marked <strong>{match[2]}</strong> as complete
				</>
			);
		}
	}
	if (item.type === "CS4" && item.actorName) {
		return (
			<>
				<strong>{item.actorName}’s</strong> payment statement is ready to check
			</>
		);
	}
	if (campaign && item.title.includes(campaign)) {
		const [before, ...rest] = item.title.split(campaign);
		return (
			<>
				{before}
				<strong>{campaign}</strong>
				{rest.join(campaign)}
			</>
		);
	}
	return item.title;
}

function displayBody(item: NotificationItem): string {
	if (item.type === "CS1") {
		if (item.body?.includes("can now be assigned")) return item.body;
		const firstName = item.actorName?.trim().split(/\s+/)[0];
		const campaign = item.campaignName?.trim();
		if (firstName && campaign) {
			return `${firstName} has confirmed participation for ${campaign}. They can now be assigned deliverables.`;
		}
	}
	if (item.type === "CS2") {
		if (item.body?.includes("Consider sourcing a backup creator")) return item.body;
		const firstName = item.actorName?.trim().split(/\s+/)[0];
		if (firstName) {
			return `${firstName} is unavailable. Consider sourcing a backup creator.`;
		}
	}
	if (item.type === "C2") {
		return "Your creator fee and engagement terms have been set. Review and accept to move into production.";
	}
	return item.body;
}

function displayCta(item: NotificationItem): string {
	if (item.type === "CS1") return "View Creator";
	if (item.type === "CS2") return "Find Replacement";
	if (item.type === "C1") return "View Campaign";
	if (item.type === "C2") return "Review Terms";
	if (item.type === "C3") return "View Deliverable";
	if (item.type === "C4") return "View Remittance";
	return item.ctaLabel?.replace(/\s*→\s*$/, "") || "Open";
}

function metaEntity(item: NotificationItem): string | undefined {
	if (item.type === "C1") return item.actorName || item.campaignName;
	return item.campaignName || item.actorName;
}

function formatPaymentDate(value: string): string {
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return value;
	return new Intl.DateTimeFormat("en-US", {
		month: "long",
		day: "numeric",
		year: "numeric",
	}).format(date);
}

function paymentStatementSummaryLabel(item: NotificationItem): string {
	const statement = item.paymentStatement!;
	const amount = new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(statement.amount);
	return `${statement.statementNumber} • ${amount} ${statement.currency}`;
}

function paymentSummaryLabel(item: NotificationItem): string {
	const payment = item.payment!;
	if (payment.reference) return `Ref: ${payment.reference}`;
	const amount = new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(payment.amount);
	return `${payment.statusLabel} • ${amount} ${payment.currency}`;
}

interface NotificationPanelProps {
	open: boolean;
	onClose: () => void;
	counts: NotificationCounts;
	items: NotificationItem[];
	loadingList: boolean;
	loadingMore: boolean;
	nextCursor: string | null;
	error: string | null;
	onLoadMore: () => void;
	onMarkAllRead: () => void;
	onItemClick: (item: NotificationItem) => void;
}

function NotificationRow({ item, onClick }: { item: NotificationItem; onClick: () => void }) {
	const unread = !item.readAt;
	const avatar = avatarStyle(item);
	const emailStatus = emailStatusFor(item.type);
	const entity = metaEntity(item);
	const tone = tabTone(item);
	const body = displayBody(item);
	// CS6 follows the same card layout as CS4 (right-hand unread dot, tighter avatar gap).
	const cardLayout = item.type === "CS4" || item.type === "CS6";

	return (
		<Item type="button" $unread={unread} $paymentStatement={cardLayout} onClick={onClick}>
			{unread ? cardLayout ? <PaymentStatementUnreadDot /> : <UnreadDot /> : null}
			<AvatarWrap>
				<Avatar $bg={avatar.bg}>
					{item.type !== "CS1" && item.type !== "CS2" && item.actorAvatarUrl ? (
						<img src={item.actorAvatarUrl} alt="" />
					) : (
						avatar.fallback
					)}
				</Avatar>
				<AvatarBadge $bg={avatar.badgeBg}>{avatar.badge}</AvatarBadge>
			</AvatarWrap>
			<ItemBody>
				<Title>{displayTitle(item)}</Title>
				<Meta>
					<span>{formatRelativeTime(item.createdAt)}</span>
					{entity && (
						<>
							<MetaSep>•</MetaSep>
							<span>{entity}</span>
						</>
					)}
					{item.type !== "C4" && item.type !== "CS4" && <Tag $tone={tone}>{tabLabel(item)}</Tag>}
					{emailStatus && item.type !== "C4" && item.type !== "CS4" && (
						<EmailMeta>
							<Mail size={11} />
							{emailStatus === "also" ? "Also emailed" : "Proposed email"}
						</EmailMeta>
					)}
				</Meta>
				{body && <Body>{body}</Body>}
				{item.payment && (
					<PaymentSummary>
						<PaymentSummaryIcon $status={item.payment.status}>
							{item.payment.status === "completed" ? (
								<Check size={12} strokeWidth={2.5} />
							) : item.payment.status === "on_hold" ? (
								<Clock3 size={12} strokeWidth={2.25} />
							) : (
								<ReceiptText size={12} strokeWidth={2.25} />
							)}
						</PaymentSummaryIcon>
						<PaymentSummaryMain>{paymentSummaryLabel(item)}</PaymentSummaryMain>
						<PaymentSummaryDate>{formatPaymentDate(item.payment.effectiveAt)}</PaymentSummaryDate>
					</PaymentSummary>
				)}
				{item.paymentStatement && (
					<PaymentStatementSummary>
						<PaymentStatementFileIcon>
							<img src={paymentStatementFileIcon} alt="" width={14} height={14} />
						</PaymentStatementFileIcon>
						<PaymentSummaryMain>{paymentStatementSummaryLabel(item)}</PaymentSummaryMain>
						<PaymentSummaryDate>{formatPaymentDate(item.paymentStatement.submittedAt)}</PaymentSummaryDate>
					</PaymentStatementSummary>
				)}
				{item.payment?.reason && <PaymentReason>Finance reason: {item.payment.reason}</PaymentReason>}
				{item.ctaLabel && item.type !== "C4" && item.type !== "CS4" && item.type !== "CS6" && (
					<CtaRow>
						<Cta>{displayCta(item)}</Cta>
					</CtaRow>
				)}
			</ItemBody>
		</Item>
	);
}

export function NotificationPanel({
	open,
	onClose,
	counts,
	items,
	loadingList,
	loadingMore,
	nextCursor,
	error,
	onLoadMore,
	onMarkAllRead,
	onItemClick,
}: NotificationPanelProps) {
	if (!open) return null;

	const hasUnread = counts.totalUnread > 0;

	return (
		<Panel role="dialog" aria-label="Notifications">
			<Header>
				<HeaderLeft>
					<HeaderTitle>Notifications</HeaderTitle>
					{counts.totalUnread > 0 && (
						<HeaderCount>{counts.totalUnread > 99 ? "99+" : counts.totalUnread}</HeaderCount>
					)}
				</HeaderLeft>
				<CloseButton onClick={onClose} aria-label="Close notifications">
					<X size={18} />
				</CloseButton>
			</Header>

			<List>
				{loadingList ? (
					<Empty>
						<Spin />
					</Empty>
				) : error ? (
					<Empty>{error}</Empty>
				) : items.length === 0 ? (
					<Empty>You're all caught up</Empty>
				) : (
					[...items]
						.sort((a, b) => {
							const aTime = a.createdAt || "";
							const bTime = b.createdAt || "";
							if (aTime !== bTime) return bTime.localeCompare(aTime);
							return (b.id || "").localeCompare(a.id || "");
						})
						.map((item) => <NotificationRow key={item.id} item={item} onClick={() => onItemClick(item)} />)
				)}
			</List>

			<Footer>
				{nextCursor && (
					<LoadMore onClick={onLoadMore} disabled={loadingMore}>
						{loadingMore ? "Loading..." : "Load more"}
					</LoadMore>
				)}
				<MarkAllButton $footer onClick={onMarkAllRead} disabled={!hasUnread}>
					Mark all as read
				</MarkAllButton>
			</Footer>
		</Panel>
	);
}
